<template>
  <v-content class="pa-0 ma-0">
    <ConfirmationDialog
      v-model="lockQuestionnairesConfirmationDialogOpened"
      @confirm="lockSelected()"
    >
      Are you sure you want to lock these questionnaires? Locking prevents any
      additional edits to the questionnaires.
    </ConfirmationDialog>

    <v-dialog
      v-model="editDueDatesDialogOpened"
      width="400"
      persistent
      @keydown.esc="editDueDatesDialogOpened = false"
    >
      <v-card block flat>
        <v-card-title>
          <span class="title"
            >Change Due Date ({{ selectedQuestionnaires.length }})</span
          >
          <v-spacer />
        </v-card-title>
        <v-card-text>
          <v-alert
            v-if="isEmpty(selectedQuestionnaires)"
            :value="true"
            outline
            block
            :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
            color="primary"
            class="text-xs-left mx-2"
          >
            Leaving the due date blank will remove due date & automated
            reminders.
          </v-alert>
          <v-layout column>
            <v-layout class="text-xs-left">
              <v-flex xs12 class="pr-2">
                <div>
                  Due Date<v-tooltip top
                    ><v-icon slot="activator" size="15" class="ml-1"
                      >info</v-icon
                    ><span
                      >Reminders will be sent out automatically to incomplete
                      questionnaire approaching the due date</span
                    ></v-tooltip
                  >
                </div>
                <v-text-field
                  class="default-input body-1"
                  type="date"
                  solo
                  item-text="name"
                  item-value="id"
                  :rules="rules.dueDateRules"
                  v-model="dueDate"
                />
              </v-flex>
            </v-layout>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn class="primary text-none" @click="changeDueDates()">
            Save
          </v-btn>
          <v-btn
            class="error--text text-none"
            flat
            @click="editDueDatesDialogOpened = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <send-questionnaire-reminder-dialog
      :opened.sync="sendQuestionnaireReminderDialogOpened"
      :selected-questionnaires="selectedQuestionnaires"
      @refresh-questionnaires="
        $emit('refresh-questionnaires');
        selectedQuestionnaires = [];
      "
    />
    <v-alert
      v-if="isEmpty(selectedQuestionnaires)"
      :value="true"
      outline
      block
      :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
      color="primary"
      class="text-xs-left mx-2"
    >
      Please select questionnaires to take action
    </v-alert>
    <v-layout v-else>
      <v-spacer />
      <v-btn
        flat
        class="my-0 white primary text-capitalize"
        @click="sendQuestionnaireReminderDialogOpened = true"
        :disabled="isEmpty(selectedQuestionnaires)"
      >
        Send reminder ({{ selectedQuestionnaires.length }})
      </v-btn>
      <v-btn
        outline
        class="my-0 primary--text text-capitalize"
        @click="lockQuestionnairesConfirmationDialogOpened = true"
        :disabled="isEmpty(selectedQuestionnaires)"
      >
        Lock ({{ selectedQuestionnaires.length }})
      </v-btn>
      <v-btn
        outline
        class="my-0 primary--text text-capitalize"
        @click="editDueDatesDialogOpened = true"
        :disabled="isEmpty(selectedQuestionnaires)"
      >
        Change due date ({{ selectedQuestionnaires.length }})
      </v-btn>
    </v-layout>
    <v-data-table
      :items="questionnaires"
      v-model="selectedQuestionnaires"
      :headers="
        onlyUnstarted
          ? unstartedQuestionnaireHeaders
          : startedQuestionnaireHeaders
      "
      disable-initial-sort
      item-key="id"
      select-all
    >
      <template slot="items" slot-scope="props">
        <tr :key="props.index">
          <td>
            <v-checkbox
              v-model="props.selected"
              primary
              color="lightgrey"
              hide-details
            ></v-checkbox>
          </td>
          <td
            class="text-xs-left"
            style="cursor: pointer"
            @click="openQuestionnaire(props.item)"
          >
            {{ props.item.formatted_name }}
          </td>
          <td
            class="text-xs-left"
            style="cursor: pointer"
            @click="viewClient(props.item.company_id)"
          >
            {{ getNameAndUserOfCompany(props.item.company_id) }}
          </td>
          <td class="text-xs-left">
            {{ getContact(props.item.company_id) }}
          </td>
          <td class="text-xs-left" v-if="onlyUnstarted">
            {{
              props.item.created_at
                ? moment(props.item.created_at).format('MM/DD/YYYY')
                : '-'
            }}
          </td>
          <td class="text-xs-left" v-else>
            {{
              props.item.updated_at_by_client
                ? moment(props.item.updated_at_by_client).format(
                    'MM/DD/YYYY - h:mm a',
                  )
                : '-'
            }}
          </td>
          <td class="text-xs-left">
            {{
              props.item.last_followed_up
                ? moment(props.item.last_followed_up).format('MM/DD/YYYY')
                : '-'
            }}
          </td>
          <td class="text-xs-left">
            {{
              props.item.due_date
                ? moment(props.item.due_date).format('MM/DD/YYYY')
                : '-'
            }}
          </td>
        </tr>
      </template>

      <template slot="no-data">
        <v-layout class="text-xs-center grey--text">
          <v-flex>
            <v-icon size="16" color="grey"> priority_high </v-icon>
            <span class="ml-1">None</span>
          </v-flex>
        </v-layout>
      </template>
    </v-data-table>
  </v-content>
</template>

<script>
import API from '@/api';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import { rules } from '@/constants/variables';
import { mapState, mapGetters } from 'vuex';
import SendQuestionnaireReminderDialog from '@/components/dialogs/SendQuestionnaireReminderDialog';
import ConfirmationDialog from '@/components/ConfirmationDialog';

export default {
  name: 'QuestionnairesTable',
  props: {
    questionnaires: {
      type: Array,
      default: [],
    },
    onlyUnstarted: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState('companies', ['companies']),
  },
  components: {
    SendQuestionnaireReminderDialog,
    ConfirmationDialog,
  },
  data() {
    return {
      moment,
      rules,
      dueDate: null,
      editDueDatesDialogOpened: false,
      selectedQuestionnaires: [],
      sendQuestionnaireReminderDialogOpened: false,
      lockQuestionnairesConfirmationDialogOpened: false,
      startedQuestionnaireHeaders: [
        {
          text: 'Name (Click to open)',
          align: 'left',
          sortable: true,
          value: 'formatted_name',
          width: '30%',
        },
        {
          text: 'Entity',
          align: 'left',
          sortable: true,
          value: 'company.name',
          width: '15%',
        },
        {
          text: 'Contact',
          align: 'left',
          sortable: true,
          value: 'company.user.email',
          width: '20%',
        },
        {
          text: 'Last Client Activity',
          align: 'left',
          value: 'updated_at_by_client',
          sortable: true,
          width: '20%',
        },
        {
          text: 'Last Reminded',
          align: 'left',
          value: 'last_followed_up',
          sortable: true,
          width: '20%',
        },
        {
          text: 'Due Date',
          align: 'left',
          value: 'due_date',
          sortable: true,
          width: '20%',
        },
      ],
      unstartedQuestionnaireHeaders: [
        {
          text: 'Name (Click to open)',
          align: 'left',
          sortable: true,
          value: 'formatted_name',
          width: '30%',
        },
        {
          text: 'Entity',
          align: 'left',
          sortable: true,
          value: 'company.name',
          width: '15%',
        },
        {
          text: 'Contact',
          align: 'left',
          sortable: true,
          value: 'company.user.email',
          width: '20%',
        },
        {
          text: 'First Sent',
          align: 'left',
          value: 'created_at',
          sortable: true,
          width: '20%',
        },
        {
          text: 'Last Reminded',
          align: 'left',
          value: 'last_followed_up',
          sortable: true,
          width: '20%',
        },
        {
          text: 'Due Date',
          align: 'left',
          value: 'due_date',
          sortable: true,
          width: '20%',
        },
      ],
    };
  },
  methods: {
    isEmpty,
    getNameAndUserOfCompany(company_id) {
      let company = this.companies.find(c => c.id === company_id);

      if (company) {
        if (company.entity_type === 'Individual') {
          return `${company.user.full_name}`;
        } else {
          return `${company.name} (${company.user.full_name})`;
        }
      }
      return 'Missing Name';
    },
    getContact(company_id) {
      let company = this.companies.find(c => c.id === company_id);

      if (company) {
        if (!isEmpty(company.user.phone_number)) {
          return `${company.user.email} (${company.user.phone_number})`;
        } else {
          return `${company.user.email}`;
        }
      }
      return '-';
    },
    viewClient(companyId) {
      window.open(`/admin-dashboard?companyId=${companyId}`, '_blank');
    },
    openQuestionnaire(questionnaire) {
      window.open(
        `/admin-dashboard?companyId=${questionnaire.company_id}&questionnaireId=${questionnaire.id}`,
        '_blank',
      );
    },
    async changeDueDates() {
      try {
        const response = await API.questionnaire.saveQuestionnaires({
          questionnaireIds: this.selectedQuestionnaires.map(q => q.id),
          dueDate: this.dueDate,
        });
        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'You have saved the questionnaires.',
          });
          this.$emit('update:opened', false);
          this.$emit('refresh-questionnaires');
        }
      } catch (e) {
        console.log(e);
      } finally {
        this.editDueDatesDialogOpened = false;
      }
    },
    async lockSelected() {
      try {
        const response1 = await API.questionnaire.lockQuestionnaires({
          questionnaireIds: this.selectedQuestionnaires.map(q => q.id),
        });
        if (response1.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response1.data.error,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'You have locked the questionnaires.',
          });
          this.selectedQuestionnaires = [];
          this.$emit('refresh-questionnaires');
        }
      } catch (e) {
        console.log(e);
      } finally {
        this.lockQuestionnairesConfirmationDialogOpened = false;
      }
    },
  },
};
</script>
